import { useEffect, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import {
  apiErrorMessage,
  enableSessionShare,
  getRecruiterInvite,
  resendInviteEmail,
  revokeSessionShare,
  downloadFile,
  fetchBlob,
  saveBlob,
  recordingExtension,
  type InviteDetail as InviteDetailData,
} from '@/api/client'
import { AppShell } from '@/components/layout/AppShell'
import { statusBadge, formatDateTime, initials, isExpired } from '@/lib/dashboard-utils'
import { ReportView } from '@/components/interview/ReportView'
import { Toaster } from '@/components/ui/toaster'
import { useToast } from '@/hooks/use-toast'
import { usePageMeta } from '@/hooks/use-page-meta';

export function InviteDetail() {
  usePageMeta("Interview invite — Aura Recruiter")
  const { inviteId } = useParams<{ inviteId: string }>()
  const { toast } = useToast()
  const [invite, setInvite] = useState<InviteDetailData | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [audioUrl, setAudioUrl] = useState<string | null>(null)
  // The loaded blob itself: the audio download saves these bytes instead of
  // re-fetching them from the artifact endpoint.
  const [audioBlob, setAudioBlob] = useState<Blob | null>(null)
  const [audioExt, setAudioExt] = useState('webm')
  const [downloading, setDownloading] = useState<string | null>(null)
  const [copiedLink, setCopiedLink] = useState(false)
  const [resending, setResending] = useState(false)
  // Share state overrides the invite payload once the recruiter enables or
  // revokes — until then the rendered state follows the fetched detail.
  const [share, setShare] = useState<{ enabled: boolean; token: string | null } | null>(null)
  const [shareBusy, setShareBusy] = useState(false)
  const [copiedShare, setCopiedShare] = useState(false)
  const [shareError, setShareError] = useState<string | null>(null)

  useEffect(() => {
    if (!inviteId) return
    let cancelled = false
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setInvite(null)
    setError(null)
    setShare(null)
    setShareError(null)
    getRecruiterInvite(inviteId)
      .then(data => { if (!cancelled) setInvite(data) })
      .catch(err => {
        if (cancelled) return
        if (err?.response?.status === 401 || err?.response?.status === 403) {
          setError("You don't have access to this interview.")
        } else if (err?.response?.status === 404) {
          setError('Interview not found.')
        } else {
          setError('Failed to load the interview.')
        }
      })
    return () => { cancelled = true }
  }, [inviteId])

  // Load the recording as an authenticated blob so it can be played inline
  useEffect(() => {
    if (!invite?.session_id || invite.status !== 'completed') return
    let objectUrl: string | null = null
    // Use the shared client (fetchBlob): it carries the base URL and auth
    // handling, unlike a hand-rolled axios call with a manual localStorage token.
    fetchBlob(`/download/${invite.session_id}/audio`)
      .then((blob: Blob) => {
        objectUrl = URL.createObjectURL(blob)
        setAudioBlob(blob)
        setAudioUrl(objectUrl)
        setAudioExt(recordingExtension(blob))
      })
      .catch(() => setAudioUrl(null))
    return () => { if (objectUrl) URL.revokeObjectURL(objectUrl) }
  }, [invite])

  const download = async (fileType: 'pdf' | 'audio') => {
    if (!invite?.session_id) return
    setDownloading(fileType)
    try {
      if (fileType === 'pdf') {
        await downloadFile(`/download/${invite.session_id}/pdf`, `interview-report-${invite.candidate_name || 'candidate'}.pdf`)
      } else {
        // The button is disabled until the blob loads (the loaded-blob gating),
        // so this is a belt-and-braces guard, not the expected path.
        if (!audioBlob) throw new Error('Recording not loaded yet')
        await saveBlob(audioBlob, `interview-recording-${invite.candidate_name || 'candidate'}.${audioExt}`)
      }
    } catch {
      setError('Download failed. The file may not be ready.')
    } finally {
      setDownloading(null)
    }
  }

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text)
    } catch {
      window.prompt('Copy this link:', text)
    }
  }

  const shareReport = async () => {
    if (!invite?.session_id) return
    setShareBusy(true)
    setShareError(null)
    try {
      const info = await enableSessionShare(invite.session_id)
      setShare({ enabled: info.share_enabled, token: info.share_token })
      if (info.share_token) {
        // The button's whole job is getting the link out — copy on enable so
        // the recruiter can paste it straight into an email.
        await copyToClipboard(`${window.location.origin}/share/report/${info.share_token}`)
        setCopiedShare(true)
        setTimeout(() => setCopiedShare(false), 2000)
      }
    } catch (err) {
      setShareError(apiErrorMessage(err, 'Could not create the share link. Please try again.'))
    } finally {
      setShareBusy(false)
    }
  }

  const revokeShare = async () => {
    if (!invite?.session_id) return
    setShareBusy(true)
    setShareError(null)
    try {
      const info = await revokeSessionShare(invite.session_id)
      setShare({ enabled: info.share_enabled, token: info.share_token })
    } catch (err) {
      setShareError(apiErrorMessage(err, 'Could not revoke the share link. Please try again.'))
    } finally {
      setShareBusy(false)
    }
  }

  if (error) {
    return (
      <AppShell active="recruiter">
        <section className="panel">
          <div className="empty">
            <h3>{error}</h3>
            <div className="empty-cta">
              <Link to="/recruiter" className="wbtn wbtn-primary">Back to dashboard</Link>
            </div>
          </div>
        </section>
      </AppShell>
    )
  }

  if (!invite) {
    return (
      <AppShell active="recruiter">
        <div className="wloading" role="status" aria-live="polite">
          <span className="wspinner" aria-hidden="true"></span>
          <span>Loading interview…</span>
        </div>
      </AppShell>
    )
  }

  // Completed interviews get the full report view
  if (invite.status === 'completed' && invite.report) {
    // Follow the fetched share state until the recruiter enables or revokes.
    const shareEnabled = (share ?? { enabled: invite.share_enabled }).enabled
    const shareToken = (share ?? { token: invite.share_token }).token
    const copyShareLink = async () => {
      if (!shareToken) return
      await copyToClipboard(`${window.location.origin}/share/report/${shareToken}`)
      setCopiedShare(true)
      setTimeout(() => setCopiedShare(false), 2000)
    }
    return (
      <AppShell active="recruiter">
        <div>
          {/* Completed invite: the report below is candidate-anonymous, so
              restate which invite this is and what was asked before it. */}
          <Link to="/recruiter" className="link-action">← Back to dashboard</Link>
          <header className="screen-head mt-3">
            <div>
              <h1>{invite.title}</h1>
              <p>{invite.questions.length} question{invite.questions.length === 1 ? '' : 's'} asked</p>
            </div>
            {/* Share posts the report read-only at an unguessable public URL —
                for hiring managers and clients without (or with no need for)
                an account. Revoking kills the URL; re-sharing mints a fresh
                one, so an already-forwarded revoked link stays dead. */}
            <div className="screen-actions">
              {shareEnabled ? (
                <>
                  <button className="wbtn wbtn-secondary wbtn-sm" onClick={copyShareLink} disabled={shareBusy}>
                    <span className="wcopy-swap" key={copiedShare ? 'copied' : 'copy'}>
                      {copiedShare ? 'Copied!' : 'Copy share link'}
                    </span>
                  </button>
                  <button className="wbtn wbtn-danger wbtn-sm" onClick={revokeShare} disabled={shareBusy}>
                    Revoke link
                  </button>
                </>
              ) : (
                <button className="wbtn wbtn-primary wbtn-sm" onClick={shareReport} disabled={shareBusy}>
                  <span className="wcopy-swap" key={copiedShare ? 'copied' : 'share'}>
                    {copiedShare ? 'Link copied!' : 'Share report'}
                  </span>
                </button>
              )}
            </div>
            {/* Passthrough to the recruiter's own scheduler — Aura never
                interprets the URL, so it renders for any https booking page. */}
            {invite.booking_url && (
              <div className="screen-actions">
                <a className="wbtn wbtn-primary" href={invite.booking_url} target="_blank" rel="noopener">
                  Book a follow-up interview
                </a>
              </div>
            )}
          </header>
          {shareError && <p className="hint" role="alert">{shareError}</p>}
          <details className="panel mt-4">
            <summary className="wq-summary">
              Show the questions
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <polyline points="6 9 12 15 18 9" />
              </svg>
            </summary>
            <ol className="wq-list">
              {invite.questions.map((q, i) => <li key={i}>{q}</li>)}
            </ol>
          </details>
          <ReportView
            report={invite.report}
            sessionId={invite.session_id || ''}
            completedAt={invite.completed_at}
            transcript={invite.transcript}
            integrityEvents={invite.integrity_events}
            proctored={invite.proctoring}
            roleTitle={invite.title}
            headingLevel="h2"
            extraActions={
              <div className="flex flex-wrap items-center gap-2">
                {audioUrl && <audio controls src={audioUrl} className="h-9" aria-label="Interview recording" />}
                {/* Gated on the loaded blob: clicking before the load resolves
                    would save an mp4 (Safari) recording under the guessed
                    default .webm extension. */}
                <button className="btn btn-ghost btn-sm" onClick={() => download('audio')} disabled={downloading !== null || !audioUrl}>
                  {downloading === 'audio' ? 'Preparing…' : 'Download audio'}
                </button>
              </div>
            }
          />
        </div>
      </AppShell>
    )
  }

  const expiredPending = invite.status === 'pending' && isExpired(invite.expires_at)
  const badge = invite.status === 'cancelled'
    ? { cls: 'badge muted', text: 'Cancelled' }
    : expiredPending
      ? { cls: 'badge pending', text: 'Expired' }
      : statusBadge(invite.status)
  const inviteLink = `${window.location.origin}/invite/${invite.token}`
  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(inviteLink)
      setCopiedLink(true)
      setTimeout(() => setCopiedLink(false), 2000)
    } catch {
      window.prompt('Copy this link:', inviteLink)
    }
  }
  const resend = async () => {
    if (!invite) return
    setResending(true)
    try {
      const updated = await resendInviteEmail(invite.invite_id)
      setInvite({ ...invite, ...updated, report: invite.report, transcript: invite.transcript, integrity_events: invite.integrity_events })
      toast({
        title: 'Invite email re-sent',
        description: `The link and deadline are on their way to ${updated.candidate_email}.`,
      })
    } catch (err) {
      toast({
        title: 'Could not re-send',
        description: apiErrorMessage(err, 'Please try again in a moment.'),
        variant: 'destructive',
      })
    } finally {
      setResending(false)
    }
  }

  return (
    <AppShell active="recruiter">
      <Toaster />
      <div>
        <header className="screen-head">
          <div>
            <h1>{invite.title}</h1>
            <p>
              <span className={`${badge.cls} mr-2.5`}>{badge.text}</span>
              Created {formatDateTime(invite.created_at)}
              {invite.candidate_email && <> · Emailed to {invite.candidate_email}</>}
              {invite.expires_at && (
                <> · {expiredPending
                  ? 'Link expired'
                  : `Link expires ${formatDateTime(invite.expires_at)}`}</>
              )}
            </p>
          </div>
          {/* Same spent-link rule as the dashboard's copyable filter: a
              pending invite that already bound a candidate is redeemed —
              the link is dead, so don't offer copying or re-sending it. */}
          {invite.status === 'pending' && !expiredPending && !invite.candidate_user_id && (
            <div className="screen-actions">
              {invite.candidate_email && (
                <button className="wbtn wbtn-secondary" onClick={resend} disabled={resending}>
                  {resending ? 'Re-sending…' : 'Resend email'}
                </button>
              )}
              <button className="wbtn wbtn-primary" onClick={copyLink}>
                <span className="wcopy-swap" key={copiedLink ? 'copied' : 'copy'}>
                  {copiedLink ? 'Copied!' : 'Copy invite link'}
                </span>
              </button>
            </div>
          )}
        </header>

        {invite.candidate_name && (
          <section className="panel widentity mt-4">
            <div className="widentity-inner">
              <span className="avatar" aria-hidden="true">{initials(invite.candidate_name)}</span>
              <div>
                <strong className="widentity-name">{invite.candidate_name}</strong>
                <span className="hint">Started {formatDateTime(invite.created_at)}</span>
              </div>
            </div>
          </section>
        )}

        <article className="panel wdetail-card">
          {invite.context && (
            <div className="field">
              <span className="field-label">Context shared with the interviewer</span>
              <p className="wdetail-context">{invite.context}</p>
            </div>
          )}
          <div className="field">
            <span className="field-label">Questions</span>
            <ol className="wquestion-list-static">
              {invite.questions.map((q, i) => <li key={i}>{q}</li>)}
            </ol>
          </div>
          {invite.status === 'completed' && (
            <div className="wresume-actions">
              {/* Same loaded-blob gate as the report view's download: the
                  extension is MIME-derived from that blob. */}
              <button className="wbtn wbtn-primary wbtn-sm" onClick={() => download('audio')} disabled={downloading !== null || !audioUrl}>
                {downloading === 'audio' ? 'Preparing…' : 'Download audio'}
              </button>
              <button className="wbtn wbtn-secondary wbtn-sm" onClick={() => download('pdf')} disabled={downloading !== null}>
                {downloading === 'pdf' ? 'Preparing…' : 'Download PDF'}
              </button>
              {!audioUrl && (
                <span className="hint">
                  No recording yet — it may still be processing, or the candidate closed the tab before it uploaded.
                </span>
              )}
            </div>
          )}
        </article>

        <div className="form-actions is-start mt-4">
          <Link to="/recruiter" className="wbtn wbtn-secondary">Back to dashboard</Link>
        </div>
      </div>
    </AppShell>
  )
}
